import {showNotification} from 'platform/components';

import {useLazyGetSourcingCebiaReportQuery} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {ApiError} from 'shared/web';

import {isSourcingCebiaReportUrlExpired} from '../utils/isSourcingCebiaReportUrlExpired';

function getSafeReportUrl(url: string | null) {
  if (url === null) {
    return null;
  }

  try {
    const parsedUrl = new URL(url);
    return parsedUrl.protocol === 'https:' && !parsedUrl.username && !parsedUrl.password
      ? parsedUrl.href
      : null;
  } catch {
    return null;
  }
}

export function useOpenSourcingCebiaReport(externalId: string) {
  const [getReport, {isFetching}] = useLazyGetSourcingCebiaReportQuery();

  const openReport = () => {
    const tab = window.open('about:blank', '_blank');
    if (tab) {
      tab.opener = null;
    }

    getReport({externalId})
      .unwrap()
      .then((report) => {
        if (isSourcingCebiaReportUrlExpired(report.latestCompleted?.reportUrlValidTo)) {
          tab?.close();
          showNotification.error(i18n.t('entity.cebiaReport.urlExpired'));
          return;
        }

        const url = getSafeReportUrl(report.latestCompleted?.reportUrl ?? null);
        if (url === null) {
          tab?.close();
          showNotification.error(i18n.t('page.integrations.labels.reportGenerationFailed'));
          return;
        }

        if (tab) {
          tab.location.replace(url);
          tab.focus();
        } else {
          const openedTab = window.open(url, '_blank');
          if (openedTab === null) {
            showNotification.error(i18n.t('general.labels.somethingWentWrong'));
          } else {
            openedTab.opener = null;
          }
        }
      })
      .catch((error: ApiError) => {
        tab?.close();
        handleApiError(error);
      });
  };

  return {openReport, isOpening: isFetching};
}
